import { Search, X } from 'lucide-react'
import { kit } from './strings'
import './FilterBar.css'

/**
 * The control strip above a list.
 *
 * The legacy hides filtering behind a `بحث` dropdown: you pick a department, the
 * menu closes, and nothing on screen says the list is now filtered. Here the
 * controls are inline and every applied filter stays on the page as a chip you can
 * take off one at a time.
 *
 * @param {{value: string, onChange: (value: string) => void, placeholder?: string}} [search]
 * @param {{id: string, label: string, value: string, onRemove: () => void}[]} [chips]
 * @param {() => void} [onReset]   drawn as a dashed "clear" pill once a chip is up
 * @param {import('react').ReactNode} [children]  the inline filter controls
 * @param {import('react').ReactNode} [actions]   end-side actions (export, density…)
 * @param {string} [count]         result summary, parked at the inline-end of the
 *                                 chip row so "what is applied" and "what it left"
 *                                 read as one statement
 */
export default function FilterBar({
  search,
  children,
  chips = [],
  onReset,
  resetLabel = kit.cancel,
  actions,
  count,
}) {
  const hasMeta = chips.length > 0 || count

  return (
    <div className="filter-bar" data-testid="filter-bar">
      <div className="filter-bar__row">
        {search && (
          <div className="filter-bar__search">
            <Search className="filter-bar__search-icon" size={16} strokeWidth={1.75} aria-hidden="true" />
            <input
              className="filter-bar__search-input"
              type="search"
              value={search.value}
              placeholder={search.placeholder}
              onChange={(event) => search.onChange(event.target.value)}
              autoComplete="off"
            />
            {search.value && (
              <button
                type="button"
                className="filter-bar__search-clear"
                onClick={() => search.onChange('')}
                aria-label={kit.clearSearch}
              >
                <X size={13} strokeWidth={2.5} />
              </button>
            )}
          </div>
        )}

        {children && <div className="filter-bar__controls">{children}</div>}

        {actions && <div className="filter-bar__actions">{actions}</div>}
      </div>

      {hasMeta && (
        <div className="filter-bar__meta">
          {chips.length > 0 && (
            <div className="filter-bar__chips">
              {chips.map((chip) => (
                <span
                  key={chip.id}
                  className="filter-chip"
                  title={`${chip.label}: ${chip.value}`}
                  data-testid="filter-chip"
                  data-filter-key={chip.id}
                >
                  <span className="filter-chip__label">
                    <bdi>{chip.label}</bdi>
                    <span className="filter-chip__sep" aria-hidden="true">
                      ·
                    </span>
                    <span className="filter-chip__value">
                      <bdi>{chip.value}</bdi>
                    </span>
                  </span>
                  <button
                    type="button"
                    className="filter-chip__x"
                    onClick={chip.onRemove}
                    aria-label={`${kit.remove} ${chip.label}`}
                  >
                    <X size={12} strokeWidth={2.5} />
                  </button>
                </span>
              ))}

              {onReset && (
                <button type="button" className="filter-bar__reset" onClick={onReset} data-testid="filter-clear">
                  <X size={12} strokeWidth={2.5} aria-hidden="true" />
                  <bdi>{resetLabel}</bdi>
                </button>
              )}
            </div>
          )}

          {count && <span className="filter-bar__count">{count}</span>}
        </div>
      )}
    </div>
  )
}
